import { POI } from '../poi/POI';
import { TexturedMaterial } from '../materials/TexturedMaterial';
import { Logger } from '../core/Logger';
import { LodImageCache } from './LodImageCache';

interface LodEntry {
  poiId: string;
  materials: TexturedMaterial[];
  lowResUrl: string | null;
  highResUrl: string | null;
  queued: boolean;
}

/**
 * Manages texture level-of-detail: loads low-res previews first, then upgrades to high-res.
 */
export class TextureLodManager {
  private static instance: TextureLodManager | null = null;

  private readonly cache = LodImageCache.Instance;
  private readonly entries = new Map<string, LodEntry>();
  private readonly upgradeQueue: LodEntry[] = [];
  private activeUpgrades = 0;
  private maxConcurrent = 3;

  private constructor() {}

  public static get Instance(): TextureLodManager {
    if (!this.instance) {
      this.instance = new TextureLodManager();
    }
    return this.instance;
  }

  public setMaxConcurrent(count: number): void {
    this.maxConcurrent = Math.max(1, Math.floor(count));
  }

  public registerPoi(poi: POI, lowResUrl: string | null, highResUrl: string | null): void {
    const materials = this.extractMaterials(poi);
    if (materials.length === 0 || (!lowResUrl && !highResUrl)) {
      return;
    }

    const entry: LodEntry = {
      poiId: poi.id,
      materials,
      lowResUrl,
      highResUrl,
      queued: false,
    };

    this.entries.set(poi.id, entry);

    if (lowResUrl) {
      void this.applyLowResTexture(entry);
    }

    if (highResUrl) {
      this.enqueueUpgrade(entry);
    }
  }

  public removePoi(poiId: string): void {
    const entry = this.entries.get(poiId);
    if (!entry) return;
    this.entries.delete(poiId);
  }

  public startQueuedUpgrades(): void {
    this.processQueue();
  }

  private async applyLowResTexture(entry: LodEntry): Promise<void> {
    if (!entry.lowResUrl) return;
    try {
      const objectUrl = await this.cache.getObjectUrl(entry.lowResUrl);
      entry.materials.forEach((material) => {
        if (typeof (material as any).setUri === 'function') {
          material.setUri(objectUrl);
        }
      });
    } catch (error) {
      Logger.Instance.Warn(`LOD: failed to apply low-res texture ${entry.lowResUrl}: ${String(error)}`, 'TextureLOD');
    }
  }

  private enqueueUpgrade(entry: LodEntry): void {
    if (entry.queued) return;
    entry.queued = true;
    this.upgradeQueue.push(entry);
  }

  private processQueue(): void {
    while (this.activeUpgrades < this.maxConcurrent && this.upgradeQueue.length > 0) {
      const entry = this.upgradeQueue.shift()!;
      entry.queued = false;
      if (!entry.highResUrl) continue;

      this.activeUpgrades++;
      this.upgradeToHighRes(entry)
        .catch((error) => {
          Logger.Instance.Warn(
            `LOD: upgrade failed for ${entry.highResUrl}: ${String(error)}`,
            'TextureLOD',
          );
        })
        .finally(() => {
          this.activeUpgrades--;
          this.processQueue();
        });
    }
  }

  private async upgradeToHighRes(entry: LodEntry): Promise<void> {
    if (!entry.highResUrl) return;
    try {
      const objectUrl = await this.cache.getObjectUrl(entry.highResUrl);
      entry.materials.forEach((material) => {
        if (typeof (material as any).setUri === 'function') {
          material.setUri(objectUrl);
        }
      });
    } catch (error) {
      Logger.Instance.Warn(`LOD: failed to cache high-res texture ${entry.highResUrl}`, 'TextureLOD');
      entry.materials.forEach((material) => {
        if (typeof (material as any).setUri === 'function') {
          material.setUri(entry.highResUrl!);
        }
      });
    }
  }

  private extractMaterials(poi: POI): TexturedMaterial[] {
    const materials: TexturedMaterial[] = [];
    const mesh = (poi as any)?.shape;
    if (!mesh) return materials;

    const pushMaterial = (mat: any) => {
      if (!mat) return;
      if (typeof mat.setUri === 'function') {
        materials.push(mat as TexturedMaterial);
      }
    };

    pushMaterial(mesh.primaryMaterial);
    pushMaterial(mesh.secondaryMaterial);

    const materialSet = (mesh as any)?._materialSet;
    if (materialSet) {
      pushMaterial(materialSet.front);
      pushMaterial(materialSet.back);
      if (Array.isArray(materialSet.sides)) {
        materialSet.sides.forEach(pushMaterial);
      }
    }

    return Array.from(new Set(materials));
  }
}

